import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { CSSProperties } from "react";

// External store for center-switch overlay state.
// Uses an incrementing counter so rapid switches each produce a new snapshot,
// forcing useSyncExternalStore to re-render and reset timers.

let switchId = 0;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return switchId;
}

export function startCenterSwitch() {
  switchId++;
  listeners.forEach((l) => l());
}

export function finishCenterSwitch() {
  switchId = 0;
  listeners.forEach((l) => l());
}

/** Returns >0 while the center-switch overlay should be visible. */
export function useCenterSwitching(): number {
  return useSyncExternalStore(subscribe, getSnapshot);
}

const FADE_IN_MS = 500;

/**
 * Returns inline styles that hide the element during a center switch
 * and fade it in once the overlay disappears.
 */
export function useCenterSwitchFade(): CSSProperties {
  const switching = useCenterSwitching();
  const wasSwitching = useRef(false);
  const [phase, setPhase] = useState<"idle" | "hidden" | "fading">("idle");

  useEffect(() => {
    if (switching > 0) {
      wasSwitching.current = true;
      setPhase("hidden");
    } else if (wasSwitching.current) {
      wasSwitching.current = false;
      // Let the browser paint at opacity 0 before starting the transition
      const raf = requestAnimationFrame(() => setPhase("fading"));
      return () => cancelAnimationFrame(raf);
    }
  }, [switching]);

  useEffect(() => {
    if (phase === "fading") {
      const timer = setTimeout(() => setPhase("idle"), FADE_IN_MS);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  if (phase === "hidden") return { opacity: 0 };
  if (phase === "fading")
    return { opacity: 1, transition: `opacity ${FADE_IN_MS}ms ease-out` };
  return {};
}
